import React, { useState, useEffect } from 'react';
import ProductCard from '../../components/ProductCard';
import EmptyState from '../../components/EmptyState';
import IngredientDrawer from '../../components/IngredientDrawer';
import { supabase } from '../../lib/supabase';
import { fetchSavedProductIds, saveProduct, unsaveProduct } from '../../lib/api/saved-products';

const CATEGORIES = [
  'Personal Care',
  'Home Cleaning',
  'Baby Care',
  'Kitchen',
  'Pet Care',
  'Food & Pantry',
];

const SAFETY_RATINGS = [
  { value: 'clean',   label: 'Clean',   dotClass: 'bg-primary-500' },
  { value: 'caution', label: 'Caution', dotClass: 'bg-warning' },
  { value: 'avoid',   label: 'Avoid',   dotClass: 'bg-error' },
];

function BrowsePage({ onNavigate }) {
  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const [selectedRatings, setSelectedRatings] = useState(new Set());
  const [brandSearch, setBrandSearch] = useState('');
  const [products, setProducts] = useState([]);
  const [savedIds, setSavedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      const [{ data: productsData, error: productsError }, { data: { user } }] = await Promise.all([
        supabase.from('products').select('*').order('category'),
        supabase.auth.getUser(),
      ]);

      if (productsError) {
        setError(productsError.message);
        setLoading(false);
        return;
      }

      setProducts(productsData);

      if (user) {
        const ids = await fetchSavedProductIds(user.id);
        setSavedIds(ids);
      }

      setLoading(false);
    }
    loadData();
  }, []);

  async function handleSave(product) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    if (savedIds.has(product.id)) {
      await unsaveProduct(user.id, product.id);
      setSavedIds((prev) => { const next = new Set(prev); next.delete(product.id); return next; });
    } else {
      await saveProduct(user.id, product.id);
      setSavedIds((prev) => new Set(prev).add(product.id));
    }
  }

  function toggleCategory(cat) {
    setSelectedCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  }

  function toggleRating(rating) {
    setSelectedRatings(prev => {
      const next = new Set(prev);
      if (next.has(rating)) next.delete(rating); else next.add(rating);
      return next;
    });
  }

  function clearFilters() {
    setSelectedCategories(new Set());
    setSelectedRatings(new Set());
    setBrandSearch('');
  }

  const filtersActive = selectedCategories.size > 0 || selectedRatings.size > 0 || brandSearch.length > 0;

  const filtered = products.filter((p) => {
    const categoryMatch = selectedCategories.size === 0 || selectedCategories.has(p.category);
    const ratingMatch = selectedRatings.size === 0 || selectedRatings.has(p.safetyRating);
    const brandMatch = !brandSearch || p.brand?.toLowerCase().includes(brandSearch.toLowerCase());
    return categoryMatch && ratingMatch && brandMatch;
  });

  return (
    <div className="px-6 py-10">
      <div className="max-w-content mx-auto">

        {/* Page header */}
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-h2 text-neutral-900">Browse Products</h1>
          <button
            type="button"
            onClick={() => onNavigate('search')}
            aria-label="Go to search"
            className="p-2 text-neutral-500 hover:text-primary-500 transition-colors duration-fast focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-md"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </button>
        </div>
        <p className="text-body text-neutral-500 mb-8">
          AI-assessed products rated for ingredient safety.
        </p>

        {/* Sidebar + grid layout */}
        <div className="flex flex-col md:flex-row gap-8 items-start">

          {/* ── Filter sidebar ── */}
          <aside className="w-full md:w-52 flex-shrink-0">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-h3 font-semibold text-neutral-900">Filters</h2>
              {filtersActive && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="text-small text-primary-500 hover:text-primary-600 font-medium transition-colors duration-fast focus:outline-none"
                >
                  Clear all
                </button>
              )}
            </div>

            {/* Brand search */}
            <div className="pb-6 border-b border-neutral-200">
              <p className="text-caption font-semibold text-neutral-400 uppercase tracking-caption mb-3">Brand</p>
              <div className="relative">
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none"
                  width="15" height="15" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  type="text"
                  placeholder="Search brands..."
                  value={brandSearch}
                  onChange={e => setBrandSearch(e.target.value)}
                  className="w-full pl-8 pr-3 py-2 text-body border border-neutral-200 rounded-lg bg-surface-card placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Safety rating */}
            <div className="py-6 border-b border-neutral-200">
              <p className="text-caption font-semibold text-neutral-400 uppercase tracking-caption mb-3">Safety Rating</p>
              <div className="flex flex-col gap-3">
                {SAFETY_RATINGS.map(({ value, label, dotClass }) => (
                  <label key={value} className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={selectedRatings.has(value)}
                      onChange={() => toggleRating(value)}
                      className="w-4 h-4 rounded-sm border-neutral-300 accent-primary-500 cursor-pointer flex-shrink-0"
                    />
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dotClass}`} aria-hidden="true" />
                    <span className="text-body text-neutral-700 group-hover:text-neutral-900 transition-colors duration-fast">
                      {label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Category */}
            <div className="pt-6">
              <p className="text-caption font-semibold text-neutral-400 uppercase tracking-caption mb-3">Category</p>
              <div className="flex flex-col gap-3">
                {CATEGORIES.map((cat) => (
                  <label key={cat} className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={selectedCategories.has(cat)}
                      onChange={() => toggleCategory(cat)}
                      className="w-4 h-4 rounded-sm border-neutral-300 accent-primary-500 cursor-pointer flex-shrink-0"
                    />
                    <span className="text-body text-neutral-700 group-hover:text-neutral-900 transition-colors duration-fast">
                      {cat}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </aside>

          {/* ── Product grid ── */}
          <div className="flex-1 min-w-0">
            {loading && (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="bg-surface-card rounded-lg border border-neutral-200 p-6 flex flex-col gap-4">
                    <div className="flex justify-between gap-3">
                      <div className="flex flex-col gap-2 flex-1">
                        <div className="h-3 bg-neutral-100 rounded-md animate-pulse w-1/3" />
                        <div className="h-5 bg-neutral-100 rounded-md animate-pulse w-2/3" />
                      </div>
                      <div className="h-6 w-16 bg-neutral-100 rounded-sm animate-pulse" />
                    </div>
                    <div className="h-5 w-24 bg-neutral-100 rounded-full animate-pulse" />
                    <div className="space-y-2">
                      <div className="h-3 bg-neutral-100 rounded-md animate-pulse" />
                      <div className="h-3 bg-neutral-100 rounded-md animate-pulse w-4/5" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {error && (
              <p className="text-body text-error">Failed to load products: {error}</p>
            )}

            {!loading && !error && (
              <>
                {filtered.length > 0 && (
                  <p className="text-caption text-neutral-400 mb-4">
                    {filtered.length} {filtered.length === 1 ? 'product' : 'products'}
                  </p>
                )}

                {filtered.length === 0 ? (
                  <EmptyState
                    title="No matching products"
                    description={filtersActive
                      ? 'No products match your current filters. Try adjusting your selections.'
                      : 'No products found.'}
                    action={filtersActive ? {
                      label: 'Clear filters',
                      onClick: clearFilters,
                    } : undefined}
                  />
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                    {filtered.map((product) => (
                      <ProductCard
                        key={product.name}
                        product={product}
                        saved={savedIds.has(product.id)}
                        onSave={handleSave}
                        onClick={() => setSelectedProduct(product)}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {selectedProduct && (
        <IngredientDrawer
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </div>
  );
}

export default BrowsePage;
